import { MobileOfferCustomer } from '@numeo/types'
import { ArrowRight, Search, Users } from 'lucide-react'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { mobileOfferError, mobileOffersApi } from './api'
import { customerOfferLinkClass, customerOfferSecondaryClass, isExactCustomerIdentity, normalizeCustomerIdentity } from './customer-offer-utils'
import { OfferButton, offerFieldClass, OfferNotice, OfferSection } from './ui'

export function CustomersPage() {
    const [params] = useSearchParams()
    const [identity, setIdentity] = useState('')
    const [customers, setCustomers] = useState<MobileOfferCustomer[]>([])
    const [status, setStatus] = useState<'initial' | 'loading' | 'results' | 'error'>('initial')
    const [error, setError] = useState('')
    const sequence = useRef(0)
    useEffect(
        () => () => {
            sequence.current++
        },
        []
    )

    const search = async (event: FormEvent) => {
        event.preventDefault()
        const request = ++sequence.current
        const query = normalizeCustomerIdentity(identity)
        setCustomers([])
        setError('')
        if (!isExactCustomerIdentity(query)) {
            setStatus('initial')
            setError('Enter the exact signup email or a full international phone number beginning with +.')
            return
        }
        setStatus('loading')
        try {
            const matches = await mobileOffersApi.lookup(query)
            if (request !== sequence.current) return
            setCustomers(matches)
            setStatus('results')
        } catch (failure) {
            if (request !== sequence.current) return
            setError(mobileOfferError(failure))
            setStatus('error')
        }
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Find a customer</h1>
                <p className={`${customerOfferSecondaryClass} mt-1`}>Use their exact signup email or international phone number.</p>
            </div>
            <OfferSection title="Customer lookup">
                <form onSubmit={(event) => void search(event)} className="space-y-3">
                    <label htmlFor="customer-identity" className="block text-sm font-medium">
                        Signup email or phone number
                    </label>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <input
                            id="customer-identity"
                            className={offerFieldClass}
                            value={identity}
                            autoComplete="off"
                            spellCheck={false}
                            placeholder="Email or +country code and phone number"
                            aria-describedby="customer-identity-help"
                            aria-invalid={!!error}
                            onChange={(event) => {
                                sequence.current++
                                setIdentity(event.target.value)
                                setCustomers([])
                                setStatus('initial')
                                setError('')
                            }}
                        />
                        <OfferButton type="submit" disabled={status === 'loading' || !identity.trim()}>
                            <Search className="h-4 w-4" />
                            {status === 'loading' ? 'Searching…' : 'Search'}
                        </OfferButton>
                    </div>
                    <p id="customer-identity-help" className={customerOfferSecondaryClass}>
                        Identity information only. Subscription status and offer eligibility are not returned.
                    </p>
                    {error && <OfferNotice error>{error}</OfferNotice>}
                </form>
            </OfferSection>
            <OfferSection title={status === 'results' ? 'Matching customers' : 'Search results'}>
                {status === 'loading' && <OfferNotice>Searching exact signup identity…</OfferNotice>}
                {status === 'initial' && (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <Users className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                        <p className="font-medium">Find the right customer first</p>
                        <p className={customerOfferSecondaryClass}>Results appear here after an exact identity search.</p>
                    </div>
                )}
                {status === 'error' && <OfferNotice>Search is unavailable. Retry the search before assigning an offer.</OfferNotice>}
                {status === 'results' && !customers.length && (
                    <div className="space-y-2 py-6 text-center">
                        <h3 className="font-medium">No exact match found</h3>
                        <p className={customerOfferSecondaryClass}>Check the signup email or include the complete international phone number.</p>
                    </div>
                )}
                {!!customers.length && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-slate-200 text-slate-500 dark:border-slate-700 dark:text-slate-400">
                                <tr>
                                    <th className="px-3 py-3">Customer</th>
                                    <th className="px-3 py-3">Signup email</th>
                                    <th className="px-3 py-3">Phone</th>
                                    <th className="px-3 py-3">
                                        <span className="sr-only">Action</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                {customers.map((customer) => (
                                    <tr key={customer.userId}>
                                        <td className="px-3 py-4">
                                            <span className="font-medium">{customer.name || 'Customer'}</span>
                                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{customer.userId}</p>
                                        </td>
                                        <td className="px-3 py-4">{customer.email || '—'}</td>
                                        <td className="px-3 py-4 whitespace-nowrap">{customer.phone || '—'}</td>
                                        <td className="px-3 py-4">
                                            <Link
                                                className={`${customerOfferLinkClass} whitespace-nowrap`}
                                                to={`/billing/mobile-offers/customers/${encodeURIComponent(customer.userId)}${params.get('offerId') ? `?offerId=${encodeURIComponent(params.get('offerId') || '')}` : ''}`}
                                            >
                                                View customer
                                                <ArrowRight className="h-4 w-4" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </OfferSection>
        </div>
    )
}
